import * as Dialog from '@radix-ui/react-dialog';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { FormEvent, PropsWithChildren, ReactNode } from 'react';

import {
  ACCOUNT_OPERATION_NAMES,
  AccountVerificationPendingError,
  type AccountCanonicalRefusal,
  type AccountIdempotencyKeyFactory,
  type AccountMutationOperation,
  type AccountProfile,
  type AccountRuntimePort,
  type AccountRuntimeResult,
  type AccountSecondFactorSetup,
  type AccountSession,
  type AccountSessionRevocationResult,
} from './account-port';
import { formatMadridInstant } from './calendar';
import { LoadingRegion, SlowOperationBanner, SystemBanner, useFrozenInteraction } from './state';
import './account.scss';

interface AccountIssue {
  readonly message: string;
  readonly conflict: boolean;
  readonly pending?: true;
}

const VERIFICATION_PENDING_COPY = 'Estamos verificando tu sesión. Vuelve a intentarlo cuando termine.';

function refusalIssue(refusal: AccountCanonicalRefusal): AccountIssue {
  const messages: Record<AccountCanonicalRefusal['reason'], string> = {
    authorization_denied: 'No tienes permiso para realizar este cambio. Tu cuenta no se ha modificado.',
    conflict: 'La información ha cambiado desde que la abriste. Actualiza y revisa antes de volver a confirmar.',
    invalid_request: 'No se ha aplicado el cambio. Revisa los datos y vuelve a intentarlo.',
    not_found: 'El elemento ya no está disponible. Actualiza la información.',
    unknown: 'El servidor no ha confirmado el cambio. No lo mostramos como guardado.',
  };
  return { message: messages[refusal.reason], conflict: refusal.reason === 'conflict' };
}

const connectionIssue = (): AccountIssue => ({
  message: 'No se ha podido confirmar el cambio. Reintenta con los mismos datos para consultar el resultado de este intento.',
  conflict: false,
});

const PROFILE_SUBJECT_FAILURE = 'No se ha podido verificar que estos datos pertenecen a tu cuenta. No se muestran ni se permiten cambios. Vuelve a cargar tu perfil.';

/** A local response-binding failure, not an invented canonical refusal. */
class AccountProfileSubjectMismatch extends Error {}

/** One user attempt retains its key after a lost reply; editing starts a new one. */
function useAccountMutation(createIdempotencyKey: AccountIdempotencyKeyFactory, confirmed = true) {
  const frozen = useFrozenInteraction();
  const lifecycle = useRef(0);
  const locked = useRef(false);
  const attempt = useRef<{ operation: AccountMutationOperation; key: string } | null>(null);
  const [pending, setPending] = useState<{ key: string; startedAtMs: number } | null>(null);
  const [issue, setIssue] = useState<AccountIssue | null>(null);

  useEffect(() => () => {
    lifecycle.current += 1;
    attempt.current = null;
  }, []);

  const invalidate = () => {
    if (locked.current) return;
    attempt.current = null;
    setIssue(null);
  };

  const execute = async <Value,>(
    operation: AccountMutationOperation,
    invoke: (idempotencyKey: string) => Promise<AccountRuntimeResult<Value>>,
  ): Promise<Value | null> => {
    if (locked.current || frozen || !confirmed) return null;
    const current = lifecycle.current;
    locked.current = true;
    setIssue(null);
    try {
      if (attempt.current?.operation !== operation) attempt.current = { operation, key: createIdempotencyKey(operation) };
      setPending({ key: attempt.current.key, startedAtMs: Date.now() });
      const result = await invoke(attempt.current.key);
      // A response belongs only to the mounted principal/block that sent it.
      if (current !== lifecycle.current) return null;
      if (result.kind !== 'success') {
        setIssue(refusalIssue(result));
        return null;
      }
      attempt.current = null;
      return result.canonical;
    } catch (error) {
      if (current === lifecycle.current) setIssue(error instanceof AccountVerificationPendingError
        ? { message: VERIFICATION_PENDING_COPY, conflict: false, pending: true } : connectionIssue());
      return null;
    } finally {
      if (current === lifecycle.current) {
        locked.current = false;
        setPending(null);
      }
    }
  };

  return { execute, invalidate, issue, pending, busy: pending !== null };
}

function useAccountRead<Value>(read: () => Promise<AccountRuntimeResult<Value>>, enabled = true) {
  const frozen = useFrozenInteraction();
  const active = useRef(true);
  const sequence = useRef(0);
  const [state, setState] = useState<{
    status: 'loading' | 'ready' | 'error' | 'pending';
    value: Value | null;
    message: string | null;
  }>({ status: 'loading', value: null, message: null });

  useEffect(() => {
    active.current = true;
    return () => { active.current = false; sequence.current += 1; };
  }, []);

  const refresh = useCallback(async (): Promise<Value | null> => {
    if (!enabled || frozen || !active.current) return null;
    const current = ++sequence.current;
    setState((previous) => ({ ...previous, status: 'loading', message: null }));
    try {
      const result = await read();
      if (current !== sequence.current) return null;
      if (result.kind === 'success') {
        setState({ status: 'ready', value: result.canonical, message: null });
        return result.canonical;
      }
      setState((previous) => ({
        ...previous,
        status: 'error',
        message: result.reason === 'authorization_denied'
          ? 'No se ha autorizado esta lectura. No se han concedido permisos nuevos.'
          : 'No se ha podido actualizar esta información. Reintenta antes de realizar cambios.',
      }));
    } catch (error) {
      if (current === sequence.current) setState((previous) => error instanceof AccountVerificationPendingError
        ? { ...previous, status: 'pending', message: VERIFICATION_PENDING_COPY }
        : error instanceof AccountProfileSubjectMismatch
          ? { status: 'error', value: null, message: PROFILE_SUBJECT_FAILURE }
          : { ...previous, status: 'error', message: 'No se ha podido cargar esta información. Revisa la conexión y reintenta.' });
    }
    return null;
  }, [enabled, frozen, read]);

  useEffect(() => {
    if (enabled) { if (!frozen) void refresh(); }
    else setState({ status: 'loading', value: null, message: null });
    return () => { sequence.current += 1; };
  }, [enabled, frozen, refresh]);

  const accept = (value: Value) => {
    sequence.current += 1;
    setState({ status: 'ready', value, message: null });
  };
  const reject = (message: string) => {
    sequence.current += 1;
    setState({ status: 'error', value: null, message });
  };
  return { ...state, refresh, accept, reject };
}

function MutationFeedback({ mutation }: { readonly mutation: ReturnType<typeof useAccountMutation> }) {
  return <>
    {mutation.issue !== null && <SystemBanner tone={mutation.issue.pending ? 'info' : 'critical'} urgent={!mutation.issue.pending}>{mutation.issue.message}</SystemBanner>}
    <SlowOperationBanner operation={mutation.pending} />
  </>;
}

/** v20's positive/neutral status pill, without the oversized semantic glyph. */
function AccountStatusTag({ on, children }: PropsWithChildren<{ readonly on: boolean }>) {
  return <span className={`state ${on ? 'state--positive' : 'tag--neutral'} account__status`}>
    {on && <svg aria-hidden="true" className="account__status-glyph" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M4 12.5l5 5L20 6.5" /></svg>}
    {children}
  </span>;
}

interface AccountDialogRecovery {
  readonly status: 'loading' | 'pending' | 'error';
  readonly message: string;
  readonly onRetry: () => Promise<unknown>;
}

function AccountRecovery({ recovery, locked, onClose }: {
  readonly recovery: AccountDialogRecovery; readonly locked: boolean; readonly onClose: () => void;
}) {
  return <div>
    <SystemBanner tone={recovery.status === 'error' ? 'critical' : 'info'} urgent={recovery.status === 'error'}>{recovery.message}</SystemBanner>
    <div className="dialog-content__actions">
      <button className="btn btn--secondary" disabled={recovery.status === 'loading'} onClick={() => void recovery.onRetry()} type="button">Reintentar perfil</button>
      <button className="btn btn--ghost" disabled={locked} onClick={onClose} type="button">Cerrar sin guardar</button>
    </div>
  </div>;
}

interface AccountDialogProps extends PropsWithChildren {
  readonly title: string;
  readonly description: string;
  readonly protected?: boolean;
  readonly onClose: () => void;
  readonly returnFocus: HTMLElement | null;
  readonly className?: string;
  readonly backdropSourceControl?: string;
  readonly sourceControl?: string;
  readonly recovery: AccountDialogRecovery | null;
}

function AccountDialog({ title, description, protected: protectedState = false, onClose, returnFocus, className = '', backdropSourceControl, sourceControl, recovery, children }: AccountDialogProps) {
  const frozen = useFrozenInteraction();
  return (
    <Dialog.Root open onOpenChange={(open) => { if (!open && !protectedState && !frozen) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" data-source-control={backdropSourceControl} data-testid="account-dialog-backdrop" />
        <Dialog.Content
          className={`dialog-content account-dialog ${className}`}
          data-source-control={sourceControl}
          aria-disabled={frozen || undefined}
          inert={frozen || undefined}
          onCloseAutoFocus={(event) => { event.preventDefault(); returnFocus?.focus(); }}
          onEscapeKeyDown={(event) => { if (protectedState || frozen) event.preventDefault(); }}
          onPointerDownOutside={(event) => { if (protectedState || frozen) event.preventDefault(); }}
        >
          <Dialog.Title className="dialog-content__title">{title}</Dialog.Title>
          <Dialog.Description className="dialog-content__description">{description}</Dialog.Description>
          {recovery !== null && <AccountRecovery locked={protectedState} onClose={onClose} recovery={recovery} />}
          <div aria-disabled={recovery !== null || undefined} className="account-dialog__work" inert={recovery !== null || undefined}>
            {children}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * D-160 ruling 11 — profile, two-step verification and the AI key are inline
 * blocks, as v20 draws them. The protections the modals carried stay inside the
 * block: the opener that collapses it is locked while a mutation is in flight or
 * while a generated secret is on screen, and the work area goes inert while the
 * profile read is unresolved, so no attempt is sent against an unknown version.
 */
function AccountInlineBlock({ eyebrow, testId, recovery, locked, onClose, children }: PropsWithChildren<{
  readonly eyebrow: string;
  readonly testId: string;
  readonly recovery: AccountDialogRecovery | null;
  readonly locked: boolean;
  readonly onClose: () => void;
}>) {
  const eyebrowId = useId();
  return <div aria-labelledby={eyebrowId} className="account-block" data-testid={testId} role="group">
    <span className="account-block__eyebrow" id={eyebrowId}>{eyebrow}</span>
    {recovery !== null && <AccountRecovery locked={locked} onClose={onClose} recovery={recovery} />}
    <div aria-disabled={recovery !== null || undefined} className="account-block__work" inert={recovery !== null || undefined}>
      {children}
    </div>
  </div>;
}

interface AccountBlockDependencies {
  readonly port: AccountRuntimePort;
  readonly createIdempotencyKey: AccountIdempotencyKeyFactory;
  readonly onClose: () => void;
  readonly recovery: AccountDialogRecovery | null;
}

type AccountDialogDependencies = AccountBlockDependencies & { readonly returnFocus: HTMLElement | null };

function ProfileBlock({ port, createIdempotencyKey, onClose, recovery, profile, onSaved, onRefresh, onIdentityMismatch, onLock, profileReady }: AccountBlockDependencies & {
  readonly profile: AccountProfile;
  readonly profileReady: boolean;
  readonly onSaved: (profile: AccountProfile) => void;
  readonly onRefresh: () => Promise<AccountProfile | null>;
  readonly onIdentityMismatch: () => void;
  readonly onLock: (locked: boolean) => void;
}) {
  const [name, setName] = useState(profile.name);
  const [avatar, setAvatar] = useState<File | null>(null);
  const [removeAvatar, setRemoveAvatar] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const mutation = useAccountMutation(createIdempotencyKey, recovery === null);
  const inputId = useId();
  // The protected dismissal of the retired modal: while the save is in flight
  // neither opener can collapse the block and drop the attempt.
  useEffect(() => { onLock(mutation.busy); return () => onLock(false); }, [mutation.busy, onLock]);

  useEffect(() => {
    if (avatar === null) { setPreview(null); return undefined; }
    const url = URL.createObjectURL(avatar);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [avatar]);

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!name.trim() || name.trim().length > 255 || fileError !== null || !profileReady) return;
    const result = await mutation.execute(ACCOUNT_OPERATION_NAMES.updateProfile, (idempotencyKey) => port.updateProfile({
      operation: ACCOUNT_OPERATION_NAMES.updateProfile,
      params: {},
      body: { name: name.trim(), ...(removeAvatar ? { avatar_url: null } : {}) },
      expectedVersion: profile.authority_version,
      idempotencyKey,
      ...(avatar === null ? {} : { files: { avatar } }),
    }));
    if (result !== null) {
      if (result.result.subject_user_id !== profile.subject_user_id) { onIdentityMismatch(); return; }
      onSaved(result.result);
    }
  };

  return (
    <AccountInlineBlock eyebrow="Editar perfil" locked={mutation.busy} onClose={onClose} recovery={recovery} testId="account-profile-block">
      <form className="account-block__form" onSubmit={(event) => void save(event)}>
        <MutationFeedback mutation={mutation} />
        <label className="field">
          <span className="field__label">Nombre</span>
          <input autoComplete="name" className="field__control" disabled={mutation.busy} maxLength={255} onChange={(event) => { mutation.invalidate(); setName(event.currentTarget.value); }} required value={name} />
        </label>
        <div className="account-block__photo">
          {(preview ?? (!removeAvatar ? profile.avatar_url : null)) !== null && <img alt="Vista previa de tu foto" src={preview ?? profile.avatar_url ?? undefined} />}
          <label className="field" htmlFor={inputId}>
            <span className="field__label">Foto</span>
            <input
              accept="image/jpeg,image/png,image/webp"
              aria-describedby={`${inputId}-help`}
              disabled={mutation.busy}
              id={inputId}
              onChange={(event) => {
                mutation.invalidate();
                const file = event.currentTarget.files?.[0] ?? null;
                if (file !== null && (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 5 * 1024 * 1024)) {
                  setFileError('Elige una foto JPG, PNG o WebP de hasta 5 MB.');
                  setAvatar(null);
                  return;
                }
                setFileError(null);
                setAvatar(file);
                setRemoveAvatar(false);
              }}
              type="file"
            />
          </label>
          <span className="account__muted" id={`${inputId}-help`}>JPG, PNG o WebP · hasta 5 MB</span>
        </div>
        <div className="account-block__photo-actions">
          {(profile.avatar_url !== null || avatar !== null) && <button className="btn btn--ghost" disabled={mutation.busy || removeAvatar} onClick={() => { mutation.invalidate(); setAvatar(null); setRemoveAvatar(true); setFileError(null); }} type="button">Quitar foto</button>}
          {removeAvatar && <span className="account__help" role="status">La foto se quitará al guardar.</span>}
        </div>
        {fileError !== null && <SystemBanner tone="critical" urgent>{fileError}</SystemBanner>}
        {mutation.issue?.conflict === true && <div><button className="btn btn--secondary" disabled={mutation.busy} onClick={() => { void onRefresh().then((current) => { if (current !== null) mutation.invalidate(); }); }} type="button">Actualizar perfil</button></div>}
        <div className="account-block__actions">
          <button className="btn btn--primary" disabled={mutation.busy || mutation.issue?.conflict === true || !profileReady || !name.trim() || fileError !== null} type="submit">{mutation.busy ? 'Guardando…' : 'Guardar cambios'}</button>
          <button className="btn btn--ghost" disabled={mutation.busy} onClick={onClose} type="button">Cancelar</button>
        </div>
      </form>
    </AccountInlineBlock>
  );
}

const PIN_FIELDS = [
  { key: 'current', label: 'PIN actual', autoComplete: 'current-password' },
  { key: 'next', label: 'PIN nuevo', autoComplete: 'new-password' },
  { key: 'repeat', label: 'Repite el PIN nuevo', autoComplete: 'new-password' },
] as const;

// D-102 / claim #6: any PIN accepted at first login must remain editable here.
// A new PIN has exactly six digits (the backend's rule; the generated contract refuses
// any other length). The current PIN is the one the person signs in with, which may be
// an older PIN of 4 to 10 digits.
const NEW_PIN_DIGITS = 6;
const CURRENT_PIN_MAX_DIGITS = 10;
const PIN_RANGE_COPY = '6 dígitos · sin letras ni símbolos';
const CURRENT_PIN_PATTERN = /^\d{4,10}$/;
const NEW_PIN_PATTERN = /^\d{6}$/;
const pinMaxDigits = (key: 'current' | 'next' | 'repeat'): number => (key === 'current' ? CURRENT_PIN_MAX_DIGITS : NEW_PIN_DIGITS);

function PinDialog({ port, createIdempotencyKey, onClose, returnFocus, recovery, onSaved }: AccountDialogDependencies & { readonly onSaved: () => void }) {
  const [pin, setPin] = useState({ current: '', next: '', repeat: '' });
  const [focused, setFocused] = useState<keyof typeof pin>('current');
  const mutation = useAccountMutation(createIdempotencyKey, recovery === null);
  const fields = useRef<Partial<Record<keyof typeof pin, HTMLInputElement | null>>>({});
  const mismatch = pin.repeat.length >= NEW_PIN_DIGITS && pin.next !== pin.repeat;
  const valid = CURRENT_PIN_PATTERN.test(pin.current) && NEW_PIN_PATTERN.test(pin.next) && pin.next === pin.repeat;
  const edit = (key: keyof typeof pin, value: string) => {
    mutation.invalidate();
    setPin((current) => ({ ...current, [key]: value.replace(/\D/g, '').slice(0, pinMaxDigits(key)) }));
  };
  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!valid) return;
    const result = await mutation.execute(ACCOUNT_OPERATION_NAMES.updateCredential, (idempotencyKey) => port.updateCredential({
      operation: ACCOUNT_OPERATION_NAMES.updateCredential,
      params: {},
      body: { current_credential: pin.current, new_pin: pin.next },
      idempotencyKey,
    }));
    if (result !== null) {
      setPin({ current: '', next: '', repeat: '' });
      onSaved();
    }
  };

  return (
    <AccountDialog backdropSourceControl="configuration-DOM-C016" className="account-dialog--pin" description={`${PIN_RANGE_COPY}.`} onClose={onClose} protected={mutation.busy} recovery={recovery} returnFocus={returnFocus} sourceControl="configuration-DOM-C017" title="Cambiar el PIN">
      <form className="account-dialog__form" onSubmit={(event) => void save(event)}>
        <MutationFeedback mutation={mutation} />
        <div className="account-pin__fields">
          {PIN_FIELDS.map((field) => <label className="account-pin__field" data-focused={focused === field.key} data-source-control="configuration-DOM-C018" key={field.key}>
            <span>{field.label}</span>
            <input
              aria-invalid={field.key === 'repeat' && mismatch}
              autoComplete={field.autoComplete}
              disabled={mutation.busy}
              inputMode="numeric"
              maxLength={pinMaxDigits(field.key)}
              onChange={(event) => edit(field.key, event.currentTarget.value)}
              onFocus={() => setFocused(field.key)}
              pattern={field.key === 'current' ? '[0-9]{4,10}' : '[0-9]{6}'}
              placeholder="—"
              ref={(element) => { fields.current[field.key] = element; }}
              required
              type="password"
              value={pin[field.key]}
            />
          </label>)}
        </div>
        <div aria-label="Teclado numérico" className="account-pin__keys" role="group">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'].map((key) => key === ''
            ? <span aria-hidden="true" className="account-pin__spacer" key="space" />
            : <button
              aria-label={key === '⌫' ? 'Borrar último dígito' : `Dígito ${key}`}
              className="btn btn--secondary"
              data-source-control="configuration-DOM-C019"
              disabled={mutation.busy}
              key={key}
              onClick={() => {
                mutation.invalidate();
                setPin((current) => ({ ...current, [focused]: key === '⌫' ? current[focused].slice(0, -1) : (current[focused] + key).slice(0, pinMaxDigits(focused)) }));
                fields.current[focused]?.focus();
              }}
              type="button"
            >{key}</button>)}
        </div>
        {mismatch && <SystemBanner tone="critical" urgent>Los dos PIN nuevos no coinciden.</SystemBanner>}
        <p className="banner-warn account__band"><span aria-hidden="true" className="account__band-glyph">!</span>Al cambiar el PIN se cierran las demás sesiones.</p>
        <div className="dialog-content__actions">
          <button className="btn btn--ghost" data-source-control="configuration-DOM-C020" disabled={mutation.busy} onClick={onClose} type="button">Cancelar</button>
          <button className="btn btn--primary" data-source-control="configuration-DOM-C021" disabled={mutation.busy || !valid} type="submit">{mutation.busy ? 'Guardando…' : 'Guardar el PIN'}</button>
        </div>
      </form>
    </AccountDialog>
  );
}

/** The Identity owner's refusal when enrollment meets an already-enabled factor. */
const SECOND_FACTOR_ALREADY_ENABLED = 'STAFF_SECOND_FACTOR_ALREADY_ENABLED';
const SECOND_FACTOR_ALREADY_ENABLED_NOTICE = 'La verificación en dos pasos ya está activa en tu cuenta. Para cambiar de aplicación, desactívala o borra el secreto con un código de la aplicación actual.';

function refusesEnabledSecondFactor<Value>(result: AccountRuntimeResult<Value>): boolean {
  if (result.kind !== 'canonical_refusal') return false;
  const errors: unknown = result.error.errors;
  return typeof errors === 'object' && errors !== null && !Array.isArray(errors)
    && (errors as { readonly errorCode?: unknown }).errorCode === SECOND_FACTOR_ALREADY_ENABLED;
}

function SecondFactorBlock({ port, createIdempotencyKey, onClose, recovery, profile, onChanged, onAlreadyEnabled, onLock }: AccountBlockDependencies & {
  readonly profile: AccountProfile;
  readonly onChanged: () => void;
  /** Another tab or device finished enrollment first: drop this setup and re-read. */
  readonly onAlreadyEnabled: () => void;
  readonly onLock: (locked: boolean) => void;
}) {
  const [setup, setSetup] = useState<AccountSecondFactorSetup | null>(null);
  const [mode, setMode] = useState<'manage' | 'disable' | 'delete'>('manage');
  const [code, setCode] = useState('');
  const mutation = useAccountMutation(createIdempotencyKey, recovery === null);
  const alreadyEnabled = useRef(false);
  const watchEnrollment = async <Value,>(reply: Promise<AccountRuntimeResult<Value>>): Promise<AccountRuntimeResult<Value>> => {
    const result = await reply;
    if (refusesEnabledSecondFactor(result)) alreadyEnabled.current = true;
    return result;
  };
  const settleEnrollment = (succeeded: boolean) => {
    if (succeeded || !alreadyEnabled.current) return;
    alreadyEnabled.current = false;
    setSetup(null);
    setCode('');
    onAlreadyEnabled();
  };
  const validCode = /^\d{6}$/.test(code);
  // The secret-visible lock of the retired modal: while a key is on screen or a
  // confirmation is in flight the block cannot be collapsed by mistake.
  const locked = mutation.busy || setup !== null;
  useEffect(() => { onLock(locked); return () => onLock(false); }, [locked, onLock]);

  const generate = async () => {
    alreadyEnabled.current = false;
    const result = await mutation.execute(ACCOUNT_OPERATION_NAMES.generateSecondFactor, (idempotencyKey) => watchEnrollment(port.generateSecondFactor({
      operation: ACCOUNT_OPERATION_NAMES.generateSecondFactor, params: {}, body: {}, idempotencyKey,
    })));
    if (result !== null) { setSetup(result); setCode(''); }
    settleEnrollment(result !== null);
  };

  const confirm = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validCode) return;
    let changed = false;
    if (mode === 'disable') {
      changed = await mutation.execute(ACCOUNT_OPERATION_NAMES.disableSecondFactor, (idempotencyKey) => port.disableSecondFactor({
        operation: ACCOUNT_OPERATION_NAMES.disableSecondFactor, params: {}, body: { code }, idempotencyKey,
      })) !== null;
    } else if (mode === 'delete') {
      changed = await mutation.execute(ACCOUNT_OPERATION_NAMES.deleteSecondFactorSecret, (idempotencyKey) => port.deleteSecondFactorSecret({
        operation: ACCOUNT_OPERATION_NAMES.deleteSecondFactorSecret, params: {}, body: { code }, idempotencyKey,
      })) !== null;
    } else if (setup !== null) {
      alreadyEnabled.current = false;
      changed = await mutation.execute(ACCOUNT_OPERATION_NAMES.enableSecondFactor, (idempotencyKey) => watchEnrollment(port.enableSecondFactor({
        operation: ACCOUNT_OPERATION_NAMES.enableSecondFactor, params: {}, body: { code }, idempotencyKey,
      }))) !== null;
      settleEnrollment(changed);
    }
    if (changed) { setSetup(null); setCode(''); onChanged(); }
  };

  const confirming = setup !== null || mode !== 'manage';
  const eyebrow = mode !== 'manage' ? 'Confirmar' : profile.two_factor_enabled ? 'Gestionar la verificación' : 'Activar la verificación';
  const description = mode === 'disable'
    ? 'Dejarás de pedir el código al entrar. La clave se conserva hasta que la borres. Confirma con un código de tu aplicación.'
    : mode === 'delete'
      ? 'La verificación se desactiva y esta clave deja de servir. Tendrás que configurar una nueva para volver a activarla. Confirma con un código de tu aplicación.'
      : 'Usa una aplicación de autenticación para añadir un código de seis dígitos al inicio de sesión.';

  return (
    <AccountInlineBlock eyebrow={eyebrow} locked={locked} onClose={onClose} recovery={recovery} testId="account-second-factor-block">
      <form className="account-block__form" onSubmit={(event) => void confirm(event)}>
        <MutationFeedback mutation={mutation} />
        {mode === 'manage' && !profile.two_factor_enabled && setup === null && <>
          <p className="account__help">{description}</p>
          <div><button className="btn btn--primary" disabled={mutation.busy} onClick={() => void generate()} type="button">{mutation.busy ? 'Generando…' : 'Generar clave de verificación'}</button></div>
        </>}
        {mode === 'manage' && profile.two_factor_enabled && <div className="account-block__actions">
          <button className="btn btn--secondary" disabled={mutation.busy} onClick={() => { mutation.invalidate(); setMode('disable'); setCode(''); }} type="button">Desactivar verificación</button>
          <button className="btn btn--ghost account-block__danger" disabled={mutation.busy} onClick={() => { mutation.invalidate(); setMode('delete'); setCode(''); }} type="button">Borrar el secreto</button>
        </div>}
        {mode !== 'manage' && <p className="account__help">{description}</p>}
        {setup !== null && <div className="account-2fa__setup">
          {setup.qrCode.startsWith('data:image/png;base64,') && <img alt="QR para configurar la verificación en dos pasos" src={setup.qrCode} />}
          <div className="account-2fa__key">
            <p className="account__help">Escanea este QR o introduce la clave manualmente en tu aplicación.</p>
            <label className="field">
              <span className="field__label">Clave manual</span>
              <input autoComplete="off" className="field__control account-2fa__secret" readOnly value={setup.manualEntryKey} />
            </label>
          </div>
        </div>}
        {setup !== null && <p className="banner-warn account__band"><span aria-hidden="true" className="account__band-glyph">!</span>La clave solo permanece en esta pantalla. La verificación no se activa hasta confirmar un código.</p>}
        {confirming && <div className="account-block__confirm">
          <label className="field">
            <span className="field__label">Código de verificación</span>
            <input autoComplete="one-time-code" className="field__control account-2fa__code" disabled={mutation.busy} inputMode="numeric" maxLength={6} onChange={(event) => { mutation.invalidate(); setCode(event.currentTarget.value.replace(/\D/g, '').slice(0, 6)); }} pattern="[0-9]{6}" placeholder="000000" required value={code} />
          </label>
          <button className={`btn ${mode === 'manage' ? 'btn--primary' : 'btn--critical'}`} disabled={mutation.busy || !validCode} type="submit">{mutation.busy ? 'Confirmando…' : mode === 'disable' ? 'Confirmar desactivación' : mode === 'delete' ? 'Borrar el secreto y desactivar' : 'Activar verificación'}</button>
          <button className="btn btn--ghost" disabled={mutation.busy} onClick={onClose} type="button">Cancelar</button>
        </div>}
      </form>
    </AccountInlineBlock>
  );
}

const DEVICE_LABEL: Record<AccountSession['device_kind'], string> = {
  desktop: 'Ordenador', mobile: 'Teléfono', tablet: 'Tableta', other: 'Otro aparato', unknown: 'Aparato desconocido',
};
const BROWSER_LABEL: Record<AccountSession['browser_family'], string> = {
  chrome: 'Chrome', edge: 'Edge', firefox: 'Firefox', other: 'Otro navegador', safari: 'Safari', unknown: 'Navegador desconocido',
};
const AUTHENTICATION_LABEL: Record<AccountSession['authentication_method'], string> = {
  passkey: 'Clave de acceso', password: 'Credencial de acceso', unknown: 'Método desconocido',
};
const DEVICE_GLYPH: Record<AccountSession['device_kind'], ReactNode> = {
  desktop: <><rect height="14" rx="2" width="20" x="2" y="3" /><path d="M8 21h8M12 17v4" /></>,
  mobile: <><rect height="20" rx="2" width="14" x="5" y="2" /><path d="M12 18h.01" /></>,
  tablet: <><rect height="20" rx="2" width="16" x="4" y="2" /><path d="M12 18h.01" /></>,
  other: <><rect height="18" rx="2" width="18" x="3" y="3" /><path d="M8 12h8" /></>,
  unknown: <><rect height="18" rx="2" width="18" x="3" y="3" /><path d="M8 12h8" /></>,
};

function accountSessionLabel(session: AccountSession): string {
  return `${DEVICE_LABEL[session.device_kind]} · ${BROWSER_LABEL[session.browser_family]}`;
}

function AccountSessionTime({ value }: { readonly value: string }) {
  if (!Number.isFinite(Date.parse(value))) return <span>Sin fecha disponible</span>;
  return <time dateTime={value}>{formatMadridInstant(value, { dateStyle: 'short', timeStyle: 'short' })}</time>;
}

function RevokeSessionsDialog({ port, createIdempotencyKey, onClose, returnFocus, recovery, selected, sessions, onRevoked, onRefresh }: AccountDialogDependencies & {
  readonly selected: AccountSession | 'all';
  readonly sessions: readonly AccountSession[];
  readonly onRevoked: (result: AccountSessionRevocationResult) => void;
  readonly onRefresh: () => Promise<unknown>;
}) {
  const mutation = useAccountMutation(createIdempotencyKey, recovery === null);
  const all = selected === 'all';
  const title = all ? 'Cerrar todas las sesiones' : 'Cerrar esta sesión';
  const confirm = async () => {
    const result = all
      ? await mutation.execute(ACCOUNT_OPERATION_NAMES.revokeAllSessions, (idempotencyKey) => port.revokeAllSessions({
        operation: ACCOUNT_OPERATION_NAMES.revokeAllSessions,
        params: {},
        body: { expected_sessions: sessions.map(({ session_id, generation }) => ({ session_id, generation })) },
        idempotencyKey,
      }))
      : await mutation.execute(ACCOUNT_OPERATION_NAMES.revokeSession, (idempotencyKey) => port.revokeSession({
        operation: ACCOUNT_OPERATION_NAMES.revokeSession,
        params: { sessionId: selected.session_id },
        body: {},
        expectedVersion: selected.generation,
        idempotencyKey,
      }));
    if (result !== null) onRevoked(result);
  };
  return (
    <AccountDialog {...(all ? { backdropSourceControl: 'configuration-DOM-C022', sourceControl: 'configuration-DOM-C023' } : {})} className={all ? 'account-dialog--all-sessions' : ''} description={all
      ? 'Se cierran todas, incluida la de este dispositivo: saldrás del Panel ahora mismo y tendrás que volver a entrar con tu PIN. No existe la opción de cerrar solo las demás.'
      : `${accountSessionLabel(selected)} tendrá que volver a entrar. No se cerrarán las demás sesiones.`}
    onClose={onClose} protected={mutation.busy} recovery={recovery} returnFocus={returnFocus} title={title}>
      <MutationFeedback mutation={mutation} />
      {mutation.issue?.conflict === true && <button className="btn btn--secondary" disabled={mutation.busy} onClick={() => { void onRefresh(); onClose(); }} type="button">Actualizar sesiones</button>}
      <div className="dialog-content__actions">
        <button className="btn btn--ghost" data-source-control={all ? 'configuration-DOM-C024' : undefined} disabled={mutation.busy} onClick={onClose} type="button">Cancelar</button>
        <button className="btn btn--primary" data-source-control={all ? 'configuration-DOM-C025' : undefined} disabled={mutation.busy || mutation.issue?.conflict === true} onClick={() => void confirm()} type="button">{mutation.busy ? 'Cerrando…' : all ? 'Cerrar todas, incluida esta' : 'Cerrar sesión'}</button>
      </div>
    </AccountDialog>
  );
}

export interface AccountSettingsProps {
  readonly port: AccountRuntimePort;
  /** Authenticated principal binding. Director compositions must provide it. */
  readonly subjectUserId?: string;
  /** Derived by the authenticated composition; never used as authorization. */
  readonly roleLabel: string;
  /** The composition supplies its confirmed access read; the heading stays shared. */
  readonly accessContent?: ReactNode;
  /** Restaurant compositions may navigate to the existing translation matrix. */
  readonly onTranslations?: () => void;
  readonly createIdempotencyKey: AccountIdempotencyKeyFactory;
  /** Local teardown after revoking this block's own session; the runtime clears the session itself. */
  readonly onSignedOut?: () => void;
  /** The Identity runtime's one logout (row 5.4b, PS-08): «Cambiar de persona»
   * never signs out through a second path. The runtime boundary reports a
   * failed logout and offers its retry. */
  readonly onLogout: () => Promise<void>;
}

/** The one «Mi cuenta» binding of a session (row 5.4b, PS-11): every page
 * that draws Mi cuenta receives it from the host, never composes its own. */
export type AccountBinding = Pick<AccountSettingsProps, 'port' | 'subjectUserId' | 'roleLabel' | 'createIdempotencyKey' | 'onLogout'>;

/** Shared Mi cuenta block. The caller owns its restaurant or director page frame. */
export function AccountSettings(props: AccountSettingsProps) {
  // Reset credentials, blocks, reads and attempt keys synchronously on a
  // principal change, even when the runtime keeps the same port object.
  return <BoundAccountSettings key={props.subjectUserId ?? 'authenticated-self'} {...props} />;
}

function BoundAccountSettings({ port, subjectUserId, roleLabel, accessContent, onTranslations, createIdempotencyKey, onSignedOut, onLogout }: AccountSettingsProps) {
  const readProfile = useCallback(async () => {
    const result = await port.readProfile({ operation: ACCOUNT_OPERATION_NAMES.readProfile, params: {} });
    if (result.kind === 'success' && subjectUserId !== undefined && result.canonical.subject_user_id !== subjectUserId) throw new AccountProfileSubjectMismatch();
    return result;
  }, [port, subjectUserId]);
  const readSessions = useCallback(() => port.readSessions({ operation: ACCOUNT_OPERATION_NAMES.readSessions, params: {} }), [port]);
  const profile = useAccountRead(readProfile);
  const profileConfirmed = profile.status === 'ready' && profile.value !== null;
  // No session request precedes a matching profile. An already-bound profile
  // remains known while refreshing; a mismatch clears it and cancels its reads.
  const sessions = useAccountRead(readSessions, profile.value !== null);
  const [leaving, setLeaving] = useState(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const changePerson = () => {
    if (leaving) return;
    setLeaving(true);
    void onLogout().finally(() => { if (mounted.current) setLeaving(false); });
  };
  // v20 keeps `cfgProfileEditing` and `cfg2faOpen` independent: opening one
  // block never discards the draft of the other.
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileLocked, setProfileLocked] = useState(false);
  const [secondFactorOpen, setSecondFactorOpen] = useState(false);
  const [secondFactorLocked, setSecondFactorLocked] = useState(false);
  const [dialog, setDialog] = useState<'pin' | 'all-sessions' | AccountSession | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [signedOut, setSignedOut] = useState(false);
  const returnFocus = useRef<HTMLElement | null>(null);
  const sectionId = useId();

  const closeDialog = () => setDialog(null);
  const rememberOpener = () => { returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null; };
  const openDialog = (next: NonNullable<typeof dialog>) => {
    rememberOpener();
    setNotice(null);
    setDialog(next);
  };
  const toggleProfile = () => {
    rememberOpener();
    setNotice(null);
    setProfileLocked(false);
    setProfileOpen((current) => !current);
  };
  const toggleSecondFactor = () => {
    rememberOpener();
    setNotice(null);
    setSecondFactorLocked(false);
    setSecondFactorOpen((current) => !current);
  };
  const closeProfile = () => { setProfileOpen(false); setProfileLocked(false); returnFocus.current?.focus(); };
  const closeSecondFactor = () => { setSecondFactorOpen(false); setSecondFactorLocked(false); returnFocus.current?.focus(); };
  const signOut = () => {
    setProfileOpen(false);
    setSecondFactorOpen(false);
    setDialog(null);
    setSignedOut(true);
    onSignedOut?.();
  };
  const refreshAccount = async () => {
    if (await profile.refresh() !== null) await sessions.refresh();
  };
  const revokeComplete = (result: AccountSessionRevocationResult) => {
    closeDialog();
    if (result.current_session_revoked) { signOut(); return; }
    setNotice('Sesión cerrada. Ese aparato tendrá que volver a entrar.');
    void sessions.refresh();
  };

  if (signedOut) return <SystemBanner>La sesión se ha cerrado. Vuelve a entrar.</SystemBanner>;

  const currentProfile = profile.value;
  const controlsDisabled = !profileConfirmed || leaving;
  const recovery: AccountDialogRecovery | null = profile.status === 'ready' ? null : {
    status: profile.status, message: profile.message ?? 'Actualizando mi cuenta…', onRetry: profile.refresh,
  };
  const sharedDialogProps = { port, createIdempotencyKey, onClose: closeDialog, returnFocus: returnFocus.current, recovery };
  const sharedBlockProps = { port, createIdempotencyKey, recovery };
  const sessionCount = sessions.value?.sessions.length ?? 0;

  return (
    <section aria-labelledby={sectionId} className="account-settings" data-testid="account-settings">
      <div className="account__head">
        <h2 className="account__title" id={sectionId}>Mi cuenta</h2>
        <p className="account__lead">Lo que sí puedes hacer aquí: tu perfil, tu PIN, tu verificación en dos pasos y tus sesiones.</p>
      </div>
      {notice !== null && <div className="account__notice" role="status">{notice}</div>}
      {profile.status === 'error' && <SystemBanner tone="critical" urgent>{profile.message}<button className="btn btn--secondary" onClick={() => void profile.refresh()} type="button">Reintentar perfil</button></SystemBanner>}
      {profile.status === 'pending' && <SystemBanner>{profile.message}<button className="btn btn--secondary" onClick={() => void profile.refresh()} type="button">Reintentar perfil</button></SystemBanner>}
      {profile.status === 'loading' && currentProfile === null && <LoadingRegion label="Cargando mi cuenta" />}
      {profile.status === 'loading' && currentProfile !== null && <p className="account__progress" role="status">Actualizando mi cuenta…</p>}
      {currentProfile !== null && <>
        <article className="card account__card">
          <div className="account__identity">
            <button aria-label="Tocar para cambiar la foto" className="account__avatar" data-source-control="configuration-DOM-C002" disabled={controlsDisabled || profileLocked} onClick={toggleProfile} type="button">
              {currentProfile.avatar_url === null ? <span aria-hidden="true">{currentProfile.name.trim().slice(0, 1)}</span> : <img alt="" src={currentProfile.avatar_url} />}
            </button>
            <div className="account__person"><span className="account__name">{currentProfile.name}</span><span className="account__muted">{roleLabel}</span></div>
            <button aria-expanded={profileOpen} className="btn btn--ghost" data-source-control="configuration-DOM-C003" data-testid="account-profile-edit" disabled={controlsDisabled || profileLocked} onClick={toggleProfile} type="button">Editar</button>
          </div>
          {profileOpen && <ProfileBlock {...sharedBlockProps} onClose={closeProfile} onLock={setProfileLocked} onIdentityMismatch={() => { closeProfile(); setNotice(null); profile.reject(PROFILE_SUBJECT_FAILURE); }} onRefresh={profile.refresh} onSaved={(saved) => { profile.accept(saved); closeProfile(); setNotice('Perfil guardado.'); }} profile={currentProfile} profileReady={profileConfirmed} />}
          <div className="account__row">
            <span className="account__grow"><span className="account__row-label">PIN de acceso</span><span className="account__help">{PIN_RANGE_COPY}</span></span>
            <button className="btn btn--secondary" data-source-control="configuration-DOM-C004" data-testid="account-pin-open" disabled={controlsDisabled} onClick={() => openDialog('pin')} type="button">Cambiar el PIN</button>
          </div>
          <div className="account__row">
            <span className="account__grow"><span className="account__row-label">Verificación en dos pasos</span><span className="account__help">Un código de seis dígitos al entrar, desde tu aplicación de autenticación.</span></span>
            <AccountStatusTag on={currentProfile.two_factor_enabled}>{currentProfile.two_factor_enabled ? 'Activada' : 'Desactivada'}</AccountStatusTag>
            <button aria-expanded={secondFactorOpen} className="btn btn--secondary" data-source-control="configuration-DOM-C005" data-testid="account-second-factor-open" disabled={controlsDisabled || secondFactorLocked} onClick={toggleSecondFactor} type="button">Gestionar</button>
          </div>
          {secondFactorOpen && <SecondFactorBlock {...sharedBlockProps} onChanged={() => { closeSecondFactor(); setNotice('Cambio de verificación confirmado. Actualizando el estado de la cuenta.'); void refreshAccount(); }} onAlreadyEnabled={() => { closeSecondFactor(); setNotice(SECOND_FACTOR_ALREADY_ENABLED_NOTICE); void refreshAccount(); }} onClose={closeSecondFactor} onLock={setSecondFactorLocked} profile={currentProfile} />}
        </article>
        <section aria-label="Sesiones activas" className="card account__card" data-testid="account-sessions">
          <div className="account__section-head">
            <h3 className="account__section-title">Sesiones activas</h3>
            {profileConfirmed && sessions.status === 'ready' && <span className="account__section-count">{sessionCount} {sessionCount === 1 ? 'aparato conectado' : 'aparatos conectados'}</span>}
          </div>
          {!profileConfirmed && <p role="status">Las sesiones se mostrarán cuando se confirme la identidad de tu cuenta.</p>}
          {profileConfirmed && sessions.status === 'loading' && <LoadingRegion label="Cargando sesiones activas" rows={2} />}
          {profileConfirmed && sessions.status === 'error' && <SystemBanner tone="critical" urgent>{sessions.message}<button className="btn btn--secondary" onClick={() => void sessions.refresh()} type="button">Reintentar sesiones</button></SystemBanner>}
          {profileConfirmed && sessions.status === 'pending' && <SystemBanner>{sessions.message}<button className="btn btn--secondary" onClick={() => void sessions.refresh()} type="button">Reintentar sesiones</button></SystemBanner>}
          {profileConfirmed && sessions.status === 'ready' && <div className="account__sessions">
            {sessions.value?.sessions.map((session) => <div className="account__session" key={session.session_id}>
              <span aria-hidden="true" className="account__session-icon">
                <svg fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.6" viewBox="0 0 24 24">{DEVICE_GLYPH[session.device_kind]}</svg>
              </span>
              <span className="account__session-text">
                <span className="account__session-heading">
                  <span className="account__session-name">{accountSessionLabel(session)}</span>
                  {session.current && <span className="state state--positive account__session-current">Sesión actual</span>}
                </span>
                <span className="account__session-meta">{AUTHENTICATION_LABEL[session.authentication_method]} · Inicio <AccountSessionTime value={session.started_at} /> · Caduca <AccountSessionTime value={session.expires_at} /></span>
              </span>
              {!session.current && <button aria-label={`Cerrar ${accountSessionLabel(session)}`} className="btn btn--ghost account__session-revoke" data-source-control="configuration-DOM-C006" disabled={leaving} onClick={() => openDialog(session)} type="button">Cerrar esta sesión</button>}
            </div>)}
            {sessions.value?.sessions.length === 0 && <p className="state-dep account__empty">No queda ninguna sesión abierta. Vuelve a entrar con tu PIN.</p>}
          </div>}
          <div className="account__row account__row--quiet">
            <span className="account__grow account__help">Las sesiones se identifican por tipo de aparato y navegador: no guardamos nombre del dispositivo ni ubicación. Si en el local hay varios aparatos iguales no se pueden distinguir; en ese caso, cierra todas y vuelve a entrar.</span>
            <button className="btn btn--secondary account__all-sessions" data-source-control="configuration-DOM-C007" disabled={!profileConfirmed || sessions.status !== 'ready' || sessionCount === 0 || leaving} onClick={() => openDialog('all-sessions')} type="button">Cerrar todas las sesiones</button>
          </div>
        </section>
        <section aria-label="Mi acceso" className="card account__card" data-testid="account-access">
          <div className="account__section-head">
            <h3 className="account__section-title">Mi acceso</h3>
            {accessContent !== null && <span className="state tag--neutral account__status">Solo lectura</span>}
          </div>
          {accessContent === undefined ? <>
            <span className="account__tag">{roleLabel}</span>
            <p className="account__help">Este contexto es de dirección de franquicia; no concede acceso a un restaurante.</p>
          </> : accessContent}
        </section>
        <div className="account__footer">
          <div className="account__footer-row">
            <button className="btn btn--secondary" data-source-control="configuration-DOM-C008" disabled={leaving} onClick={changePerson}
              type="button">{leaving ? 'Cerrando sesión…' : 'Cambiar de persona'}</button>
            <p className="account__language">{onTranslations === undefined ? 'El Panel funciona en español.' : <>
              El Panel funciona en español; las traducciones de la carta, en <button className="account__translation-link" data-source-control="configuration-DOM-C009" data-testid="account-translations" onClick={onTranslations} type="button">Menú → Traducciones</button>.
            </>}</p>
          </div>
        </div>
      </>}
      {currentProfile !== null && <>
        {dialog === 'pin' && <PinDialog {...sharedDialogProps} onSaved={() => { closeDialog(); setNotice('PIN cambiado. Úsalo en el próximo inicio de sesión.'); void refreshAccount(); }} />}
        {dialog !== null && dialog !== 'pin' && sessions.value !== null && <RevokeSessionsDialog {...sharedDialogProps} onRefresh={sessions.refresh} onRevoked={revokeComplete} selected={dialog === 'all-sessions' ? 'all' : dialog} sessions={sessions.value.sessions} />}
      </>}
    </section>
  );
}
